import { z } from 'zod';
import { Email, IsoDateTime, LocalDate, PageQuery, Phone, Slug, Uuid, page, withDefaults } from './common.js';
/**
 * Module `data-protection` (docs/modules/data-protection.md, ADR 0029, spec 6.2, 8.6.15). Owners: S51 (admin tools: subject
 * search, export, erasure, legal holds, retention overview), S52 (self-service requests, public pages, suppression).
 */
// --- Subjects ---------------------------------------------------------------------------------------------------------------
/** Search for a person across every module (data_protection.export or .erase). At least one field. */
export const DataSubjectSearch = z
    .object({
    email: z.string().trim().toLowerCase().max(254).optional(),
    phone: z.string().trim().max(32).optional(),
    name: z.string().trim().min(2).max(120).optional(),
    memberId: Uuid.optional(),
})
    .refine((q) => q.email || q.phone || q.name || q.memberId, { message: 'email, phone, name or memberId is required' });
/** Kinds of record a hit points at. Providers may add their own kinds (free text), these are the common ones. */
export const DataSubjectRefKind = z.enum(['member', 'visit', 'preregistration', 'email', 'phone']);
/** One candidate record. The admin ticks which hits are the person: nothing is guessed (ADR 0029). */
export const DataSubjectHit = z.object({
    module: z.string(),
    kind: z.string(),
    id: z.string(),
    /** Short label, e.g. "Visitor, Head Office, 12 Oct 2026". */
    label: z.string(),
    at: IsoDateTime.nullable(),
    siteId: Uuid.nullable(),
});
export const DataSubjectSearchResult = z.object({ hits: z.array(DataSubjectHit), truncated: z.boolean() });
/** The confirmed person: every id and contact detail that is theirs. Built from the ticked hits. */
export const DataSubject = z.object({
    memberIds: z.array(Uuid).max(50).default([]),
    visitIds: z.array(Uuid).max(5000).default([]),
    preRegistrationIds: z.array(Uuid).max(2000).default([]),
    emails: z.array(z.string().trim().toLowerCase().max(254)).max(10).default([]),
    phones: z.array(z.string().trim().max(32)).max(10).default([]),
    /** Exact names used (for free-text records such as parcels and block-list entries). */
    names: z.array(z.string().trim().max(120)).max(10).default([]),
    /** Server use only (erasure replay after a restore): `<module>:<keyed hash>` entries kept in the erasure ledger instead of addresses. */
    emailHmacs: z.array(z.string().max(200)).max(100).optional(),
});
// --- Export and erasure jobs ---------------------------------------------------------------------------------------------------
export const DataJobKind = z.enum(['export', 'erasure']);
export const DataJobStatus = z.enum(['queued', 'running', 'done', 'failed', 'blocked']);
export const DataJobModuleResult = z.object({
    module: z.string(),
    erased: z.number().int(),
    anonymised: z.number().int(),
    kept: z.number().int(),
    /** i18n key explaining a keep, e.g. `dp.keep.evacuation_law`. */
    keepReasonKey: z.string().nullable(),
    done: z.boolean(),
});
export const DataJob = z.object({
    id: Uuid,
    kind: DataJobKind,
    status: DataJobStatus,
    /** Masked: "S*** T***, s***@example.com". The erased details are never stored after erasure. */
    subjectLabel: z.string(),
    reason: z.string(),
    requestId: Uuid.nullable(),
    createdByLabel: z.string(),
    createdAt: IsoDateTime,
    finishedAt: IsoDateTime.nullable(),
    results: z.array(DataJobModuleResult),
    /** Export only: until when the ZIP can be downloaded. */
    downloadUntil: IsoDateTime.nullable(),
    errorKey: z.string().nullable(),
});
export const DataJobList = page(DataJob);
export const DataJobQuery = PageQuery.extend({ kind: DataJobKind.optional() });
export const DataExportCreate = z.object({ subject: DataSubject, reason: z.string().trim().min(1).max(500), requestId: Uuid.nullable().default(null) });
/** What erasure would do, per module, before the admin confirms. */
export const ErasurePreview = z.object({
    modules: z.array(DataJobModuleResult.omit({ done: true })),
    /** Legal holds that stop the erasure (the job would be 'blocked'). */
    holds: z.array(z.object({ id: Uuid, label: z.string() })),
});
export const ErasureCreate = z.object({
    subject: DataSubject,
    reason: z.string().trim().min(1).max(500),
    requestId: Uuid.nullable().default(null),
    confirm: z.literal(true),
});
// --- Legal holds -------------------------------------------------------------------------------------------------------------
export const LegalHold = z.object({
    id: Uuid,
    label: z.string(),
    reason: z.string(),
    memberIds: z.array(Uuid),
    visitIds: z.array(Uuid),
    createdByLabel: z.string(),
    createdAt: IsoDateTime,
    releasedAt: IsoDateTime.nullable(),
    releasedByLabel: z.string().nullable(),
});
export const LegalHoldCreate = z.object({
    label: z.string().trim().min(1).max(120),
    reason: z.string().trim().min(1).max(1000),
    memberIds: z.array(Uuid).max(50).default([]),
    visitIds: z.array(Uuid).max(5000).default([]),
});
export const LegalHoldRelease = z.object({ note: z.string().trim().max(500).default('') });
// --- Retention overview ------------------------------------------------------------------------------------------------------
export const RetentionRule = z.object({
    module: z.string(),
    /** i18n key of what is kept, e.g. `dp.retention.visits`. */
    labelKey: z.string(),
    /** Where the setting lives, for the "change" link: tenant/site/group and the settings path. */
    scope: z.enum(['tenant', 'site', 'group', 'platform']),
    settingPath: z.string(),
    /** Days; 0 = kept until deleted by hand (or per the module's own rule). Null when it varies per site or group. */
    days: z.number().int().nullable(),
});
export const RetentionRunEntry = z.object({ module: z.string(), action: z.string(), at: IsoDateTime, deleted: z.number().int() });
export const RetentionOverview = z.object({ rules: z.array(RetentionRule), recentRuns: z.array(RetentionRunEntry) });
// --- Self-service requests (S52) --------------------------------------------------------------------------------------------
export const DataRequestKind = z.enum(['access', 'rectification', 'erasure', 'stop_marketing']);
export const DataRequestStatus = z.enum(['unverified', 'open', 'approved', 'refused', 'completed', 'cancelled', 'expired']);
/** GET /data-protection/public/:tenantSlug/privacy (public). Nothing about any person. */
export const PublicPrivacyPage = z.object({
    tenantSlug: Slug,
    tenantName: z.string(),
    noticeText: z.string(),
    noticeUrl: z.string(),
    kinds: z.array(DataRequestKind),
    selfServiceEnabled: z.boolean(),
});
/** POST .../requests (public, rate-limited): sends a 6-digit code to the given address if it is on a record. Always 202. */
export const PublicDataRequestStart = z
    .object({
    kind: DataRequestKind,
    name: z.string().trim().min(1).max(120),
    email: Email.optional(),
    phone: Phone.optional(),
    /** Rectification: what is wrong and what it should be. */
    details: z.string().trim().max(2000).default(''),
    /** Honeypot: must be empty. */
    website: z.string().max(0).default(''),
})
    .refine((r) => r.email || r.phone, { message: 'email or phone is required' });
/** Always the same shape, whether or not the address is known (no enumeration). */
export const PublicDataRequestStarted = z.object({ requestId: Uuid, channel: z.enum(['email', 'sms']) });
export const PublicDataRequestVerify = z.object({ requestId: Uuid, code: z.string().regex(/^\d{6}$/) });
/** After the code: the request reference and a status link (kind `data_request`). stop_marketing completes at once. */
export const PublicDataRequestVerified = z.object({ reference: z.string(), status: DataRequestStatus, statusUrl: z.string() });
/** GET /data-protection/public/requests/:token (link kind data_request): the person's own request status. */
export const PublicDataRequestStatus = z.object({
    reference: z.string(),
    kind: DataRequestKind,
    status: DataRequestStatus,
    receivedAt: IsoDateTime,
    dueOn: LocalDate,
    decisionText: z.string().nullable(),
    /** Access requests once completed: the export link (kind data_export), valid 7 days. */
    downloadUrl: z.string().nullable(),
});
export const DataRequest = z.object({
    id: Uuid,
    reference: z.string(),
    kind: DataRequestKind,
    status: DataRequestStatus,
    name: z.string(),
    /** Masked contact used for the code; full value only with data_protection.requests. */
    contactMasked: z.string(),
    contact: z.string().nullable(),
    details: z.string(),
    receivedAt: IsoDateTime,
    verifiedAt: IsoDateTime.nullable(),
    dueOn: LocalDate.nullable(),
    daysLeft: z.number().int().nullable(),
    decidedByLabel: z.string().nullable(),
    decidedAt: IsoDateTime.nullable(),
    decisionReason: z.string().nullable(),
    refusalTemplateId: Uuid.nullable(),
    jobId: Uuid.nullable(),
    completedAt: IsoDateTime.nullable(),
    source: z.enum(['public_page', 'email_link', 'contactless', 'companion', 'portal']),
});
export const DataRequestQuery = PageQuery.extend({
    status: z.enum(['open', 'approved', 'refused', 'completed', 'all']).default('open'),
    kind: DataRequestKind.optional(),
});
export const DataRequestList = page(DataRequest);
/** Logged by an admin for a request received another way (letter, phone). Verified by the admin. */
export const DataRequestCreate = z.object({
    kind: DataRequestKind,
    name: z.string().trim().min(1).max(120),
    email: Email.nullable().default(null),
    phone: Phone.nullable().default(null),
    details: z.string().trim().max(2000).default(''),
    receivedOn: LocalDate,
});
export const DataRequestDecision = z.discriminatedUnion('decision', [
    /** Access and erasure need the confirmed subject; rectification needs the change made by hand first. */
    z.object({ decision: z.literal('approve'), subject: DataSubject.optional(), note: z.string().trim().max(1000).default('') }),
    z.object({
        decision: z.literal('refuse'),
        refusalTemplateId: Uuid.nullable().default(null),
        reason: z.string().trim().min(1).max(2000),
    }),
]);
export const RefusalTemplate = z.object({ id: Uuid, title: z.string(), text: z.string(), builtIn: z.boolean() });
export const RefusalTemplateCreate = z.object({ title: z.string().trim().min(1).max(120), text: z.string().trim().min(1).max(2000) });
export const RefusalTemplateUpdate = z.object({ title: z.string().trim().min(1).max(120).optional(), text: z.string().trim().min(1).max(2000).optional() });
export const DataRequestReportQuery = z.object({ from: LocalDate, to: LocalDate });
export const DataRequestReport = z.object({
    from: LocalDate,
    to: LocalDate,
    received: z.number().int(),
    completedOnTime: z.number().int(),
    completedLate: z.number().int(),
    refused: z.number().int(),
    open: z.number().int(),
    byKind: z.array(z.object({ kind: DataRequestKind, count: z.number().int() })),
    refusalReasons: z.array(z.object({ title: z.string(), count: z.number().int() })),
});
/** GET /data-protection/me/privacy-link (Companion access): the tenant's public privacy page, or null while self-service is off. */
export const PrivacyLink = z.object({ url: z.string().nullable() });
/** GET /data-protection/requests/report: `format` picks JSON (default), CSV or PDF. */
export const DataRequestReportDownloadQuery = DataRequestReportQuery.extend({ format: z.enum(['json', 'csv', 'pdf']).default('json') });
/** Marketing-type emails a person can stop (applied at once). Feedback requests are the only kind in Phase 4. */
export const SuppressionKind = z.enum(['feedback']);
// --- Settings -----------------------------------------------------------------------------------------------------------------
export const DataProtectionTenantSettings = z.object({
    selfService: withDefaults(z.object({
        /** Public request page, "Your data" links in emails, contactless page and app. Off until the tenant turns it on. */
        enabled: z.boolean().default(false),
        kinds: z.array(DataRequestKind).max(4).default(['access', 'rectification', 'erasure', 'stop_marketing']),
        /** Legal deadline from verification (UK GDPR: one month). */
        deadlineDays: z.number().int().min(1).max(90).default(30),
        /** Reminders to request handlers this many days before the deadline. */
        reminderDaysBefore: z.array(z.number().int().min(1).max(60)).max(4).default([7, 2]),
        noticeText: z.string().max(10000).default(''),
        noticeUrl: z.union([z.literal(''), z.url().max(500)]).default(''),
        /** Where "a request arrived" emails go when no notification rule is set. Empty = users with data_protection.requests. */
        handlerEmails: z.array(Email).max(10).default([]),
    })),
    /** Export ZIPs (admin and self-service) can be downloaded for this many days. */
    exportLinkDays: z.number().int().min(1).max(30).default(7),
    /** Erased people show as "<prefix> <n>" in records kept by law. */
    anonymisedLabelPrefix: z.string().trim().min(1).max(30).default('Visitor'),
    /** Keep finished export/erasure job records (without personal details) this many days. 0 = keep. */
    keepJobDays: z.number().int().min(0).max(3650).default(1095),
});
//# sourceMappingURL=data-protection.js.map